// Tweaks panel for ScopeWorth landing page
// Floats over the page; toggled by the host toolbar.

import React from 'react';
import {
  useTweaks,
  TweaksPanel,
  TweakSection,
  TweakSlider,
  TweakToggle,
  TweakColor,
  TweakText,
} from './tweaks-panel.jsx';

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "floatEnabled": true,
  "floatAmplitude": 10,
  "floatDuration": 6,
  "floatTilt": 1.2,
  "heroAccent": "#5EEAD4",
  "heroPrimary": "#2563EB",
  "heroHeadline": "",
  "showStamp": true
}/*EDITMODE-END*/;

export function ScopeWorthTweaks() {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);

  // Apply float controls to the hero visual element
  React.useEffect(() => {
    const el = document.querySelector('.sw-hero-visual');
    if (!el) return;
    el.style.setProperty('--float-amp', t.floatAmplitude + 'px');
    el.style.setProperty('--float-dur', t.floatDuration + 's');
    el.style.setProperty('--float-tilt', t.floatTilt + 'deg');
    el.classList.toggle('sw-no-float', !t.floatEnabled);
  }, [t.floatEnabled, t.floatAmplitude, t.floatDuration, t.floatTilt]);

  // Apply accent gradient + primary color overrides
  React.useEffect(() => {
    let style = document.getElementById('sw-tweak-overrides');
    if (!style) {
      style = document.createElement('style');
      style.id = 'sw-tweak-overrides';
      document.head.appendChild(style);
    }
    style.textContent = `
      :root { --accent-blue: ${t.heroPrimary}; }
      .sw-hero-title-accent {
        background: linear-gradient(90deg, ${t.heroAccent} 0%, #60A5FA 100%);
        -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
      }
      .sw-btn-primary { background: ${t.heroPrimary}; box-shadow: 0 1px 0 rgba(255,255,255,0.18) inset, 0 6px 18px -6px ${t.heroPrimary}88; }
    `;
  }, [t.heroPrimary, t.heroAccent]);

  // Apply headline text override
  React.useEffect(() => {
    if (!t.heroHeadline) return;
    const el = document.querySelector('.sw-hero-title');
    if (!el) return;
    const lines = (t.heroHeadline || '').split('\n');
    // Last non-empty line wraps in the accent span; if user removed newlines, leave as-is.
    if (lines.length > 1) {
      const last = lines[lines.length - 1];
      const head = lines.slice(0, -1).join('\n');
      el.innerHTML = '';
      head.split('\n').forEach((line, i, arr) => {
        el.appendChild(document.createTextNode(line));
        if (i < arr.length - 1 || true) el.appendChild(document.createElement('br'));
      });
      const span = document.createElement('span');
      span.className = 'sw-hero-title-accent';
      span.textContent = last;
      el.appendChild(span);
    } else {
      el.textContent = lines[0] || '';
    }
  }, [t.heroHeadline]);

  // Toggle the "CONFIDENTIAL · CLIENT-READY" stamp
  React.useEffect(() => {
    const stamp = document.querySelector('.sw-report-stamp');
    if (stamp) stamp.style.display = t.showStamp ? '' : 'none';
  }, [t.showStamp]);

  return (
    <TweaksPanel title="Tweaks">
      <TweakSection label="Hero report animation"/>
      <TweakToggle label="Floating motion" value={t.floatEnabled}
        onChange={(v) => setTweak('floatEnabled', v)}/>
      <TweakSlider label="Lift" value={t.floatAmplitude} min={0} max={28} step={1} unit="px"
        onChange={(v) => setTweak('floatAmplitude', v)}/>
      <TweakSlider label="Duration" value={t.floatDuration} min={2} max={14} step={0.5} unit="s"
        onChange={(v) => setTweak('floatDuration', v)}/>
      <TweakSlider label="Tilt" value={t.floatTilt} min={0} max={4} step={0.1} unit="°"
        onChange={(v) => setTweak('floatTilt', v)}/>

      <TweakSection label="Brand color"/>
      <TweakColor label="Primary action" value={t.heroPrimary}
        onChange={(v) => setTweak('heroPrimary', v)}/>
      <TweakColor label="Headline accent" value={t.heroAccent}
        onChange={(v) => setTweak('heroAccent', v)}/>

      <TweakSection label="Hero copy"/>
      <TweakText label="Headline (newline-separated)" value={t.heroHeadline}
        onChange={(v) => setTweak('heroHeadline', v)}/>
      <TweakToggle label="Show 'CLIENT-READY' stamp" value={t.showStamp}
        onChange={(v) => setTweak('showStamp', v)}/>
    </TweaksPanel>
  );
}

if (typeof window !== 'undefined') window.ScopeWorthTweaks = ScopeWorthTweaks;
